<script setup>
import { ref, computed, watch, onMounted, onUnmounted, nextTick } from 'vue'
import { useToast } from 'vue-toastification'
import { useUserStore } from '@/stores/user'
import { useDataSourcesStore } from '@product/stores/dataSources'
import Signal from '@/api/Signal'
import SavedSearches from '@/api/SavedSearches'
import SaveSearchDropdown from '@/components/EventSearch/SaveSearchDropdown.vue'
import SourceSelector from '@/components/common/SourceSelector.vue'
import { formatRelativeDate, formatCount } from '@/lib/formatDate'

const toast = useToast()
const userStore = useUserStore()
const dataSourcesStore = useDataSourcesStore()
const isAdmin = computed(() => userStore.accountType === 1337)

const props = defineProps({
  signalId: {
    type: String,
    required: true
  },
  onPinChanged: {
    type: Function,
    default: null
  }
})

const loading = ref(true)
const signal = ref(null)
const error = ref('')
const iocSort = ref('count')
const sourceFilter = ref({ sources: [], logTypes: [] })

// Ctrl+multi-select state
const ctrlHeld = ref(false)
const selectedIOCs = ref(new Set())
const popupVisible = ref(false)
const popupX = ref(0)
const popupY = ref(0)
let mouseX = 0
let mouseY = 0

// Retry state
const retrying = ref(false)

// Title edit state
const editingTitle = ref(false)
const editTitleValue = ref('')
const savingTitle = ref(false)
const titleInputRef = ref(null)

function startEditTitle() {
  editTitleValue.value = signal.value.title
  editingTitle.value = true
  nextTick(() => titleInputRef.value?.focus())
}

function cancelEditTitle() {
  editingTitle.value = false
}

async function saveTitle() {
  const newTitle = editTitleValue.value.trim()
  if (!newTitle || newTitle === signal.value.title) {
    cancelEditTitle()
    return
  }
  savingTitle.value = true
  try {
    const res = await Signal.edit(signal.value.id, 'update_title', { title: newTitle })
    if (res.status === 'success') {
      signal.value.title = newTitle
      editingTitle.value = false
    } else {
      toast.error(res.message || 'Failed to update title')
    }
  } finally {
    savingTitle.value = false
  }
}

function onTitleKeydown(e) {
  if (e.key === 'Enter') {
    e.preventDefault()
    saveTitle()
  } else if (e.key === 'Escape') {
    cancelEditTitle()
  }
}

// IOC delete state
const deletingIOC = ref(null)

async function deleteIOC(ioc) {
  if (deletingIOC.value) return
  deletingIOC.value = ioc.id
  try {
    const res = await Signal.edit(signal.value.id, 'delete_ioc', { ioc_id: ioc.id })
    if (res.status === 'success') {
      signal.value.iocs = signal.value.iocs.filter(i => i.id !== ioc.id)
      signal.value.ioc_count = Math.max(0, (signal.value.ioc_count || 0) - 1)
    } else {
      toast.error(res.message || 'Failed to delete IOC')
    }
  } finally {
    deletingIOC.value = null
  }
}

async function retrySignal() {
  if (retrying.value || !signal.value) return
  retrying.value = true
  try {
    const res = await Signal.retry(signal.value.id)
    if (res.status === 'success') {
      toast.success('Queued for reanalysis')
    } else {
      toast.error(res.message || 'Failed to queue for reanalysis')
    }
  } finally {
    retrying.value = false
  }
}

// Save search state
const saveBtnRef = ref(null)
const saveDropdownRef = ref(null)
const saveLoading = ref(false)
const deleteLoading = ref(false)
const subscriptionLoading = ref(false)
const savedSearchId = ref(null)
const savedSearchName = ref('')
const savedSearchDescription = ref('')
const savedSearchQuery = ref('')
const savedSearchFilters = ref([])
const isSubscribed = ref(false)

const iocTypeConfig = {
  1: { label: 'IP Addresses', icon: ['fas', 'globe'] },
  2: { label: 'Domains', icon: ['fas', 'globe'] },
  3: { label: 'URLs', icon: ['fas', 'link'] },
  4: { label: 'Hashes', icon: ['fas', 'hashtag'] },
  5: { label: 'Fingerprints', icon: ['fas', 'fingerprint'] },
  6: { label: 'Misc', icon: ['fas', 'tag'] },
  7: { label: 'CVEs', icon: ['fas', 'bug'] },
  8: { label: 'YARA Rules', icon: ['fas', 'shield-alt'] },
  9: { label: 'Emails', icon: ['fas', 'envelope'] },
  10: { label: 'Filenames', icon: ['fas', 'file'] },
}

const groupedIOCs = computed(() => {
  if (!signal.value?.iocs?.length) return []
  const groups = {}
  for (const ioc of signal.value.iocs) {
    if (!groups[ioc.ioc_type]) {
      groups[ioc.ioc_type] = {
        type: ioc.ioc_type,
        config: iocTypeConfig[ioc.ioc_type] || { label: 'Other', icon: ['fas', 'question'] },
        items: []
      }
    }
    groups[ioc.ioc_type].items.push(ioc)
  }
  // Sort items within each group
  for (const g of Object.values(groups)) {
    if (iocSort.value === 'last_seen') {
      g.items.sort((a, b) => {
        const aTime = a.last_seen_at ? new Date(a.last_seen_at).getTime() : 0
        const bTime = b.last_seen_at ? new Date(b.last_seen_at).getTime() : 0
        return bTime - aTime
      })
    } else {
      g.items.sort((a, b) => b.result_count - a.result_count)
    }
  }
  // Return groups ordered by type
  return Object.values(groups).sort((a, b) => a.type - b.type)
})

const summaryParagraphs = computed(() => {
  if (!signal.value?.extended_summary) return []
  return signal.value.extended_summary.split('\n').filter(p => p.trim())
})

const sourceLabel = computed(() => {
  if (!signal.value) return ''
  if (signal.value.source_type === 2) return 'PDF Upload'
  return signal.value.source_url || ''
})

const selectedIPCount = computed(() => {
  if (!signal.value?.iocs) return 0
  return signal.value.iocs.filter(ioc => selectedIOCs.value.has(ioc.id) && ioc.ioc_type === 1).length
})

const hasSelectedIPs = computed(() => selectedIPCount.value > 0)

function formatDate(ts) {
  if (!ts) return ''
  const d = new Date(ts)
  return d.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  })
}

async function togglePin() {
  if (!signal.value) return
  const wasPinned = signal.value.pinned
  signal.value.pinned = !wasPinned
  const res = wasPinned ? await Signal.unpin(signal.value.id) : await Signal.pin(signal.value.id)
  if (res.status !== 'success') {
    signal.value.pinned = wasPinned
    return
  }
  if (props.onPinChanged) props.onPinChanged({ id: signal.value.id, pinned: signal.value.pinned })
}

function isSafeURL(url) {
  if (!url) return false
  try {
    const parsed = new URL(url)
    return parsed.protocol === 'http:' || parsed.protocol === 'https:'
  } catch {
    return false
  }
}

async function copyValue(val) {
  try {
    await navigator.clipboard.writeText(val)
  } catch {
    // Fallback: no-op
  }
}

function openInEventSearch(ioc) {
  const query = encodeURIComponent(`"${ioc.value}"`)
  window.open(`/search/event?query=${query}`, '_blank')
}

// Ctrl+multi-select handlers
function onKeyDown(e) {
  if (e.key === 'Control' || e.key === 'Meta') {
    ctrlHeld.value = true
  }
  if (e.key === 'Escape' && popupVisible.value) {
    dismissPopup()
  }
}

function onKeyUp(e) {
  if (e.key === 'Control' || e.key === 'Meta') {
    ctrlHeld.value = false
    if (selectedIOCs.value.size > 0) {
      popupX.value = mouseX
      popupY.value = mouseY
      popupVisible.value = true
    }
  }
}

function onMouseMove(e) {
  mouseX = e.clientX
  mouseY = e.clientY
}

function onIOCRowClick(ioc) {
  if (ctrlHeld.value) {
    const newSet = new Set(selectedIOCs.value)
    if (newSet.has(ioc.id)) {
      newSet.delete(ioc.id)
    } else {
      newSet.add(ioc.id)
    }
    selectedIOCs.value = newSet
    return true // handled
  }
  return false
}

function dismissPopup() {
  popupVisible.value = false
  selectedIOCs.value = new Set()
}

function onClickOutsidePopup(e) {
  if (popupVisible.value) {
    const popup = document.querySelector('.ioc-popup')
    if (popup && !popup.contains(e.target)) {
      dismissPopup()
    }
  }
}

function getSelectedIOCs() {
  if (!signal.value?.iocs) return []
  return signal.value.iocs.filter(ioc => selectedIOCs.value.has(ioc.id))
}

function searchSelectedEvents() {
  const selected = getSelectedIOCs()
  if (!selected.length) return
  const query = selected.map(ioc => `"${ioc.value}"`).join(' OR ')
  window.open(`/search/event?query=${encodeURIComponent(query)}`, '_blank')
  dismissPopup()
}

function viewSelectedIP() {
  const selected = getSelectedIOCs().filter(ioc => ioc.ioc_type === 1)
  if (!selected.length) return
  if (selected.length === 1) {
    window.open(`/search/ip?input=${encodeURIComponent(selected[0].value)}&type=ip`, '_blank')
  } else {
    const ips = selected.map(ioc => encodeURIComponent(ioc.value)).join(',')
    window.open(`/search/ip?input=${ips}&type=ip_table`, '_blank')
  }
  dismissPopup()
}

// Build query from all IOCs
const iocQuery = computed(() => {
  if (!signal.value?.iocs?.length) return ''
  return signal.value.iocs.map(ioc => `"${ioc.value}"`).join(' OR ')
})

function openSaveDropdown() {
  saveDropdownRef.value?.open()
}

async function handleSaveSearch({ name, description }) {
  saveLoading.value = true
  try {
    const res = await SavedSearches.create(name, description, iocQuery.value, [],
      { signal_id: signal.value.id }
    )
    if (res.status === 'success') {
      savedSearchId.value = res.data.id
      savedSearchName.value = name
      savedSearchDescription.value = description
      savedSearchQuery.value = iocQuery.value
      savedSearchFilters.value = []
    } else {
      toast.error(res.message || 'Failed to save search')
    }
  } catch {
    toast.error('An unexpected error occurred')
  } finally {
    saveLoading.value = false
  }
}

async function handleOverwriteSearch({ name, description }) {
  saveLoading.value = true
  try {
    const res = await SavedSearches.update(savedSearchId.value, name, description, iocQuery.value, [])
    if (res.status === 'success') {
      savedSearchName.value = name
      savedSearchDescription.value = description
      savedSearchQuery.value = iocQuery.value
      savedSearchFilters.value = []
    } else {
      toast.error(res.message || 'Failed to update search')
    }
  } catch {
    toast.error('An unexpected error occurred')
  } finally {
    saveLoading.value = false
  }
}

async function handleDeleteSearch() {
  deleteLoading.value = true
  try {
    const res = await SavedSearches.delete(savedSearchId.value)
    if (res.status === 'success') {
      savedSearchId.value = null
      savedSearchName.value = ''
      savedSearchDescription.value = ''
      savedSearchQuery.value = ''
      savedSearchFilters.value = []
      isSubscribed.value = false
      saveDropdownRef.value?.close()
    } else {
      toast.error(res.message || 'Failed to delete search')
    }
  } catch {
    toast.error('An unexpected error occurred')
  } finally {
    deleteLoading.value = false
  }
}

async function handleSubscribe() {
  subscriptionLoading.value = true
  try {
    const res = await SavedSearches.subscribe(savedSearchId.value)
    if (res.status === 'success') {
      isSubscribed.value = true
    } else {
      toast.error(res.message || 'Failed to subscribe')
    }
  } catch {
    toast.error('An unexpected error occurred')
  } finally {
    subscriptionLoading.value = false
  }
}

async function handleUnsubscribe() {
  subscriptionLoading.value = true
  try {
    const res = await SavedSearches.unsubscribe(savedSearchId.value)
    if (res.status === 'success') {
      isSubscribed.value = false
    } else {
      toast.error(res.message || 'Failed to unsubscribe')
    }
  } catch {
    toast.error('An unexpected error occurred')
  } finally {
    subscriptionLoading.value = false
  }
}

async function loadSignal() {
  loading.value = true
  error.value = ''
  try {
    const res = await Signal.get(props.signalId, {
      sources: sourceFilter.value?.sources?.length ? sourceFilter.value.sources : undefined
    })
    if (res.status === 'success') {
      signal.value = res.data
      if (res.saved_search) {
        savedSearchId.value = res.saved_search.id
        savedSearchName.value = res.saved_search.name
        savedSearchDescription.value = res.saved_search.description
        savedSearchQuery.value = res.saved_search.query
        savedSearchFilters.value = res.saved_search.filters || []
        isSubscribed.value = res.saved_search.is_subscribed
      }
    } else {
      error.value = res.message || 'Failed to load signal'
    }
  } catch {
    error.value = 'An unexpected error occurred'
  } finally {
    loading.value = false
  }
}

watch(sourceFilter, () => {
  if (!signal.value) return
  loadSignal()
})

onMounted(() => {
  loadSignal()
  window.addEventListener('keydown', onKeyDown)
  window.addEventListener('keyup', onKeyUp)
  window.addEventListener('mousemove', onMouseMove)
  window.addEventListener('click', onClickOutsidePopup)
})

onUnmounted(() => {
  window.removeEventListener('keydown', onKeyDown)
  window.removeEventListener('keyup', onKeyUp)
  window.removeEventListener('mousemove', onMouseMove)
  window.removeEventListener('click', onClickOutsidePopup)
})
</script>

<template>
  <div class="signal-detail">
    <!-- Loading -->
    <div v-if="loading" class="loading-state">
      <font-awesome-icon :icon="['fas', 'spinner']" spin class="loading-icon" />
      <p>Loading signal...</p>
    </div>

    <!-- Error -->
    <div v-else-if="error" class="error-state">
      <font-awesome-icon :icon="['fas', 'exclamation-triangle']" />
      <span>{{ error }}</span>
    </div>

    <!-- Content -->
    <div v-else-if="signal" class="signal-content">
      <!-- Hero image -->
      <div v-if="isSafeURL(signal.image_url)" class="hero-image">
        <img :src="signal.image_url" :alt="signal.title" />
      </div>

      <!-- Title + meta -->
      <div class="signal-header">
        <div class="title-row">
          <template v-if="editingTitle">
            <div class="title-edit">
              <input
                v-model="editTitleValue"
                class="title-input"
                @keydown="onTitleKeydown"
                :disabled="savingTitle"
                ref="titleInputRef"
              />
              <button
                class="btn-pin-detail"
                :disabled="savingTitle"
                title="Save title"
                @click="saveTitle"
              >
                <font-awesome-icon
                  v-if="savingTitle"
                  :icon="['fas', 'spinner']"
                  spin
                />
                <font-awesome-icon v-else :icon="['fas', 'check']" />
              </button>
              <button
                class="btn-pin-detail"
                :disabled="savingTitle"
                title="Cancel"
                @click="cancelEditTitle"
              >
                <font-awesome-icon :icon="['fas', 'xmark']" />
              </button>
            </div>
          </template>
          <template v-else>
            <h2 class="signal-title">{{ signal.title }}</h2>
            <button
              v-if="isAdmin"
              class="btn-pin-detail"
              title="Edit title"
              @click="startEditTitle"
            >
              <font-awesome-icon :icon="['fas', 'pen']" />
            </button>
          </template>
          <button
            class="btn-pin-detail"
            :class="{ pinned: signal.pinned }"
            @click="togglePin"
            :title="signal.pinned ? 'Unpin' : 'Pin'"
          >
            <font-awesome-icon :icon="['fas', 'thumbtack']" />
          </button>
          <button
            v-if="isAdmin"
            class="btn-pin-detail"
            :class="{ disabled: retrying }"
            :disabled="retrying"
            title="Reanalyze signal"
            @click="retrySignal"
          >
            <font-awesome-icon
              v-if="retrying"
              :icon="['fas', 'spinner']"
              spin
            />
            <font-awesome-icon v-else :icon="['fas', 'arrow-rotate-right']" />
          </button>
          <button
            ref="saveBtnRef"
            class="btn-pin-detail"
            :class="{ saved: savedSearchId, disabled: !signal.iocs?.length }"
            :title="!signal.iocs?.length ? 'No indicators to save' : savedSearchId ? 'Saved search' : 'Save as search'"
            :disabled="!signal.iocs?.length"
            @click="openSaveDropdown"
          >
            <font-awesome-icon :icon="['fas', 'save']" />
          </button>
          <SaveSearchDropdown
            ref="saveDropdownRef"
            mode="inline"
            :show-trigger="false"
            :anchor-element="saveBtnRef"
            :query="iocQuery"
            :filters="[]"
            :default-name="signal.title || ''"
            :default-description="signal.summary || ''"
            :saved-search-id="savedSearchId"
            :saved-search-name="savedSearchName"
            :saved-search-description="savedSearchDescription"
            :saved-search-query="savedSearchQuery"
            :saved-search-filters="savedSearchFilters"
            :loading="saveLoading"
            :delete-loading="deleteLoading"
            :is-subscribed="isSubscribed"
            :subscription-loading="subscriptionLoading"
            @save="handleSaveSearch"
            @overwrite="handleOverwriteSearch"
            @delete="handleDeleteSearch"
            @subscribe="handleSubscribe"
            @unsubscribe="handleUnsubscribe"
          />
        </div>
        <div class="signal-meta">
          <span class="meta-date" title="Added">
            <font-awesome-icon :icon="['fas', 'clock']" />
            {{ formatDate(signal.created_at) }}
          </span>
          <template v-if="signal.published_at">
            <span class="meta-sep">&middot;</span>
            <span class="meta-date" title="Published">
              <font-awesome-icon :icon="['fas', 'calendar']" />
              {{ formatDate(signal.published_at) }}
            </span>
          </template>
          <template v-if="signal.source_type === 3">
            <span class="meta-sep">&middot;</span>
            <span class="meta-source source-feed">
              <font-awesome-icon :icon="['fas', 'rss']" />
              From feed
            </span>
            <span v-if="isSafeURL(signal.source_url)" class="meta-sep">&middot;</span>
            <a v-if="isSafeURL(signal.source_url)" :href="signal.source_url" target="_blank" rel="noopener" class="meta-source meta-link" title="Open source">
              <font-awesome-icon :icon="['fas', 'arrow-up-right-from-square']" />
              Source
            </a>
          </template>
          <template v-else>
            <span class="meta-sep">&middot;</span>
            <span class="meta-source source-manual">
              <font-awesome-icon :icon="['fas', 'user']" />
              Manually added
            </span>
            <template v-if="signal.source_type === 2 && isSafeURL(signal.source_url)">
              <span class="meta-sep">&middot;</span>
              <a :href="signal.source_url" target="_blank" rel="noopener" class="meta-source meta-link">
                <font-awesome-icon :icon="['fas', 'file-pdf']" />
                Download PDF
              </a>
            </template>
            <template v-else-if="isSafeURL(signal.source_url)">
              <span class="meta-sep">&middot;</span>
              <a :href="signal.source_url" target="_blank" rel="noopener" class="meta-source meta-link">
                <font-awesome-icon :icon="['fas', 'arrow-up-right-from-square']" />
                Source
              </a>
            </template>
          </template>
        </div>
      </div>

      <!-- Extended summary -->
      <div v-if="summaryParagraphs.length" class="summary-section">
        <p v-for="(p, i) in summaryParagraphs" :key="i">{{ p }}</p>
      </div>

      <!-- IOC groups -->
      <div v-if="groupedIOCs.length" class="ioc-section">
        <div class="ioc-section-header">
          <h3 class="ioc-section-title">Indicators of Compromise</h3>
          <div class="ioc-header-controls">
            <SourceSelector mode="local" v-model="sourceFilter" :show-log-types="false" size="small" />
            <div class="ioc-sort-tabs">
              <button
                class="sort-tab"
                :class="{ active: iocSort === 'count' }"
                @click="iocSort = 'count'"
              >Hits</button>
              <button
                class="sort-tab"
                :class="{ active: iocSort === 'last_seen' }"
                @click="iocSort = 'last_seen'"
              >Last Seen</button>
            </div>
          </div>
        </div>

        <div v-for="group in groupedIOCs" :key="group.type" class="ioc-group">
          <div class="ioc-group-header">
            <font-awesome-icon :icon="group.config.icon" class="ioc-group-icon" />
            <span class="ioc-group-label">{{ group.config.label }}</span>
            <span class="ioc-group-count">{{ group.items.length }}</span>
          </div>

          <div class="ioc-list">
            <div
              v-for="ioc in group.items"
              :key="ioc.id"
              class="ioc-row"
              :class="{ 'has-hits': ioc.result_count > 0, 'selected': selectedIOCs.has(ioc.id) }"
              @click="onIOCRowClick(ioc)"
            >
              <span class="ioc-value">{{ ioc.value }}</span>
              <div class="ioc-actions">
                <span v-if="ioc.last_seen_at" class="seen-badge">
                  {{ formatRelativeDate(ioc.last_seen_at) }}
                </span>
                <span v-if="ioc.result_count > 0" class="hit-badge">
                  {{ formatCount(ioc.result_count) }} hit{{ ioc.result_count !== 1 ? 's' : '' }}
                </span>
                <button class="btn-search" @click.stop="openInEventSearch(ioc)" title="Search events">
                  <font-awesome-icon :icon="['fas', 'magnifying-glass']" />
                </button>
                <button class="btn-copy" @click.stop="copyValue(ioc.value)" title="Copy">
                  <font-awesome-icon :icon="['fas', 'copy']" />
                </button>
                <button
                  v-if="isAdmin"
                  class="btn-delete-ioc"
                  :disabled="deletingIOC === ioc.id"
                  title="Delete IOC"
                  @click.stop="deleteIOC(ioc)"
                >
                  <font-awesome-icon
                    v-if="deletingIOC === ioc.id"
                    :icon="['fas', 'spinner']"
                    spin
                  />
                  <font-awesome-icon v-else :icon="['fas', 'trash']" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- No IOCs -->
      <div v-else-if="!loading" class="no-iocs">
        <font-awesome-icon :icon="['fas', 'shield-alt']" />
        <p>No indicators of compromise found</p>
      </div>
    </div>

    <!-- Multi-select popup -->
    <div
      v-if="popupVisible"
      class="ioc-popup"
      :style="{ top: popupY + 'px', left: popupX + 'px' }"
    >
      <button @click="searchSelectedEvents">
        <font-awesome-icon :icon="['fas', 'magnifying-glass']" />
        Search Events
      </button>
      <button v-if="hasSelectedIPs" @click="viewSelectedIP">
        <font-awesome-icon :icon="['fas', 'globe']" />
        {{ selectedIPCount > 1 ? 'Lookup IP Table' : 'Lookup IP' }}
      </button>
    </div>
  </div>
</template>

<style scoped>
.signal-detail {
  height: 100%;
  width: 100%;
  display: flex;
  flex-direction: column;
  color: var(--text-heading);
  overflow-y: auto;
}

.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 4rem 1rem;
  color: var(--text-muted);
}

.loading-icon {
  font-size: 2rem;
  margin-bottom: 1rem;
}

.error-state {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 1rem 2rem;
  color: var(--color-error-text);
  background: var(--color-error-soft);
  border-bottom: 1px solid rgba(239, 68, 68, 0.2);
}

.signal-content {
  display: flex;
  flex-direction: column;
}

/* Hero */
.hero-image {
  width: 100%;
  max-height: 250px;
  overflow: hidden;
  background: rgba(0, 0, 0, 0.3);
}

.hero-image img {
  width: 100%;
  height: 250px;
  object-fit: cover;
}

/* Header */
.signal-header {
  padding: 1.5rem 2rem 0;
}

.title-row {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
}

.signal-title {
  font-size: 1.375rem;
  font-weight: 600;
  color: var(--text-heading);
  margin: 0 0 0.5rem 0;
  line-height: 1.3;
  flex: 1;
}

.btn-pin-detail {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2rem;
  height: 2rem;
  background: var(--surface-overlay);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: var(--radius-sm);
  color: var(--text-faint);
  cursor: pointer;
  font-size: 0.8125rem;
  flex-shrink: 0;
  transition: var(--transition-fast);
}

.btn-pin-detail:hover:not(:disabled) {
  color: var(--accent-text);
  background: var(--accent-soft-3);
  border-color: rgba(115, 87, 210, 0.3);
}

.btn-pin-detail:disabled {
  opacity: 0.35;
  cursor: not-allowed;
}

.btn-pin-detail.pinned {
  color: var(--accent-text);
  background: var(--accent-soft-2);
  border-color: rgba(115, 87, 210, 0.2);
}

.btn-pin-detail.saved {
  color: var(--color-success);
  background: var(--color-success-soft);
  border-color: rgba(16, 185, 129, 0.2);
}

.btn-pin-detail.saved:hover {
  color: var(--color-success-text);
  background: rgba(16, 185, 129, 0.15);
  border-color: rgba(16, 185, 129, 0.3);
}

.title-edit {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex: 1;
  min-width: 0;
}

.title-input {
  flex: 1;
  min-width: 0;
  font-size: 1.375rem;
  font-weight: 600;
  color: var(--text-heading);
  background: var(--surface-overlay);
  border: 1px solid rgba(115, 87, 210, 0.4);
  border-radius: var(--radius-sm);
  padding: 0.25rem 0.5rem;
  outline: none;
  font-family: inherit;
  line-height: 1.3;
}

.title-input:focus {
  border-color: rgba(115, 87, 210, 0.6);
  background: rgba(255, 255, 255, 0.06);
}

.title-input:disabled {
  opacity: 0.5;
}

.title-row :deep(.save-dropdown-wrapper) {
  position: absolute;
  width: 0;
  height: 0;
  overflow: hidden;
  padding: 0;
}

.signal-meta {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.8125rem;
  color: var(--text-faint);
}

.meta-date {
  color: var(--text-primary);
}

.meta-sep {
  color: var(--text-disabled);
}

.meta-source {
  display: flex;
  align-items: center;
  gap: 0.375rem;
}

.meta-link {
  color: var(--accent);
  text-decoration: none;
  transition: color 0.15s;
}

.meta-link:hover {
  color: var(--accent-text);
}

.source-feed {
  color: var(--color-feed);
}

.source-manual {
  color: var(--color-info-alt);
}

/* Summary */
.summary-section {
  padding: 1.25rem 2rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
}

.summary-section p {
  font-size: 0.875rem;
  color: var(--text-primary);
  line-height: 1.7;
  margin: 0 0 0.75rem 0;
}

.summary-section p:last-child {
  margin-bottom: 0;
}

/* IOC Section */
.ioc-section {
  padding: 1.25rem 2rem 2rem;
}

.ioc-section-header {
  position: sticky;
  top: 0;
  z-index: 5;
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1rem;
  padding: 0.75rem 0;
  background: var(--surface-page);
}

.ioc-header-controls {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.ioc-section-title {
  font-size: 0.95rem;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0;
}

.ioc-sort-tabs {
  display: flex;
  align-items: center;
  background: var(--surface-overlay);
  border-radius: var(--radius-md);
  padding: 0.1875rem;
  gap: 0.125rem;
}

.sort-tab {
  padding: 0.3125rem 0.625rem;
  background: transparent;
  border: none;
  border-radius: var(--radius-sm);
  color: var(--text-secondary);
  font-size: 0.75rem;
  font-weight: 500;
  cursor: pointer;
  transition: var(--transition-fast);
  white-space: nowrap;
}

.sort-tab:hover {
  color: var(--text-primary);
  background: var(--surface-overlay);
}

.sort-tab.active {
  color: var(--text-primary);
  background: var(--surface-overlay-3);
}

.ioc-group {
  margin-bottom: 1.25rem;
}

.ioc-group:last-child {
  margin-bottom: 0;
}

.ioc-group-header {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.5rem;
}

.ioc-group-icon {
  font-size: 0.75rem;
  color: var(--text-faint);
}

.ioc-group-label {
  font-size: 0.75rem;
  font-weight: 500;
  color: var(--text-muted);
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.ioc-group-count {
  font-size: 0.6875rem;
  color: var(--text-disabled);
  background: var(--surface-overlay-2);
  padding: 0.0625rem 0.375rem;
  border-radius: var(--radius-xs);
}

.ioc-list {
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: var(--radius-md);
  overflow: hidden;
}

.ioc-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.5rem 0.75rem;
  gap: 0.75rem;
  transition: background 0.1s;
}

.ioc-row:hover {
  background: rgba(255, 255, 255, 0.02);
}

.ioc-row + .ioc-row {
  border-top: 1px solid rgba(255, 255, 255, 0.04);
}

.ioc-row.has-hits {
  border-left: 3px solid var(--accent);
}

.ioc-row.selected {
  background: rgba(115, 87, 210, 0.12);
  border-left: 3px solid var(--accent-text);
}

.ioc-value {
  font-family: 'Monaco', 'Courier New', monospace;
  font-size: 0.8125rem;
  color: var(--text-primary);
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.ioc-actions {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-shrink: 0;
}

.seen-badge {
  font-size: 0.625rem;
  color: var(--text-faint);
  white-space: nowrap;
}

.hit-badge {
  font-size: 0.6875rem;
  font-weight: 600;
  color: var(--accent-text);
  background: var(--accent-soft-3);
  padding: 0.125rem 0.5rem;
  border-radius: var(--radius-xs);
  white-space: nowrap;
}

.btn-search,
.btn-copy {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 1.5rem;
  height: 1.5rem;
  background: transparent;
  border: none;
  color: var(--text-disabled);
  cursor: pointer;
  border-radius: var(--radius-xs);
  font-size: 0.6875rem;
  transition: all 0.15s;
}

.btn-search:hover,
.btn-copy:hover {
  color: var(--text-muted);
  background: var(--surface-overlay-2);
}

.btn-delete-ioc {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 1.5rem;
  height: 1.5rem;
  background: transparent;
  border: none;
  color: var(--text-disabled);
  cursor: pointer;
  border-radius: var(--radius-xs);
  font-size: 0.6875rem;
  transition: all 0.15s;
}

.btn-delete-ioc:hover:not(:disabled) {
  color: var(--color-error-text);
  background: var(--color-error-soft);
}

.btn-delete-ioc:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.no-iocs {
  text-align: center;
  padding: 2rem;
  color: var(--text-disabled);
}

.no-iocs svg {
  font-size: 1.5rem;
  margin-bottom: 0.5rem;
  opacity: 0.5;
}

.no-iocs p {
  margin: 0;
  font-size: 0.8125rem;
}

/* Multi-select popup */
.ioc-popup {
  position: fixed;
  z-index: 9999;
  display: flex;
  flex-direction: column;
  background: var(--surface-page);
  border: 1px solid var(--border-subtle);
  border-radius: var(--radius-md);
  padding: 0.25rem;
  box-shadow: var(--shadow-lg);
  min-width: 160px;
}

.ioc-popup button {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 0.75rem;
  background: transparent;
  border: none;
  border-radius: var(--radius-sm);
  color: var(--text-primary);
  font-size: 0.8125rem;
  font-weight: 500;
  cursor: pointer;
  transition: var(--transition-fast);
  white-space: nowrap;
}

.ioc-popup button:hover {
  background: var(--accent-soft-3);
  color: var(--accent-text);
}

/* Scrollbar */
.signal-detail::-webkit-scrollbar {
  width: 6px;
}

.signal-detail::-webkit-scrollbar-track {
  background: transparent;
}

.signal-detail::-webkit-scrollbar-thumb {
  background: var(--border);
  border-radius: var(--radius-xs);
}
</style>
